import type { ReactElement } from 'react';
import { sanitizeManuscriptHtml } from './sanitizeManuscriptHtml.js';

export interface ManuscriptBodyHtmlProps {
  /** A chapter's `body` — may be plain text (generate/expand/translate
   * origin) or F67 WYSIWYG-produced markup (bold/italic/headings/lists/
   * blockquote/links). Always run through {@link sanitizeManuscriptHtml}
   * before render — see that module's doc comment for why this can't
   * trust the value as pre-sanitized. */
  html: string;
  className?: string | undefined;
}

/**
 * Renders a manuscript chapter `body` as its intended rich content — bold,
 * italic, headings, lists, links, blockquotes — instead of literal escaped
 * HTML tags. Shared by the F66 reader (`ReadPage`), the F62/F63 public
 * excerpt (`PublicShareReader`), and the F65 Theme live preview (`StylePage`)
 * so all three surfaces show the SAME formatted content the owner wrote,
 * rather than three divergent plain-text renderings.
 */
export function ManuscriptBodyHtml({ html, className }: ManuscriptBodyHtmlProps): ReactElement {
  // Sanitized immediately above via an allowlist DOM walk (sanitizeManuscriptHtml.ts) —
  // CODE_GUIDELINES §26 blocks `dangerouslySetInnerHTML` over an un-sanitized field;
  // this field IS sanitized, just client-side rather than server-side (see that
  // module's doc comment for why the server-side guarantee can't be trusted here).
  return <div className={className} dangerouslySetInnerHTML={{ __html: sanitizeManuscriptHtml(html) }} />;
}
